/**
 * Surcharge reconciliation runs — /api/admin/surcharge-reconciliation/*.
 * Types mirror app/routes/surcharge_reconciliation.py and
 * services/surcharge/reconciliation.py::ReconciliationReport (model_dump json:
 * Decimals arrive as strings).
 */
import api from './api';

export type SurchargeRunStatus = 'running' | 'completed' | 'failed';
export type SurchargeRunTrigger = 'cron' | 'manual';

export interface SurchargeRunSummary {
  id: string;
  status: SurchargeRunStatus;
  trigger: SurchargeRunTrigger;
  window_start: string;
  window_end: string;
  started_at: string;
  completed_at: string | null;
  error: string | null;
  groups_in_scope: number | null;
  expected_policy: string | null;
  billed_surcharge: string | null;
  shortfall: string | null;
  capture_pct: string | null;
  exemption_foregone: string | null;
}

export interface SurchargeOptions {
  enabled: boolean;
  percent: string;
  date_start: string | null;
  date_end: string | null;
}

export interface OrderGroup {
  customer_no: string;
  customer_name: string | null;
  customer_po_no: string;
  sales_order_nos: string[];
  invoice_nos: string[];
  order_dates: string[];
  first_invoice_date: string | null;
  warehouse_codes: string[];
  salesperson_no: string | null;
  salesperson_name: string | null;
  quote_ids: number[];
  exempt_quote_ids: number[];
  goods: string;
  quote_exempt_goods: string;
  expected_gross: string;
  expected_policy: string;
  billed_surcharge: string;
  fuel_positive: string;
  gap: string;
  classification: string;
  note: string;
  bol_scanned: boolean | null;
  in_sales_site: boolean;
  reversed_by_cm: boolean;
  customer_exempt_now: boolean;
  exemption_basis: string;
  as_of: string | null;
  /** What the sales site charged at BOL scan (order_invoice_queue.surcharge_amount), summed over the group's orders; null when no queue row. */
  queued_surcharge: string | null;
  queue_rows: number;
  /** Per-location exemptions (surcharge_exempt_ship_to, live 2026-10-30); absent on reports persisted before them. */
  ship_to_codes?: string[];
  exempt_ship_to_codes?: string[];
  ship_to_exempt_goods?: string;
  ship_to_exempt_all?: boolean;
}

export interface ExemptCustomerRow {
  customer_no: string;
  customer_name: string | null;
  customer_status: string | null;
  salesperson_no: string | null;
  salesperson_name: string | null;
  is_exempt: boolean;
  window_goods: string;
  window_invoice_count: number;
  window_surcharge_billed: string;
  window_surcharge_foregone: string;
}

export interface ExemptQuoteRow {
  quote_id: number;
  customer_no: string;
  customer_name: string | null;
  project_name: string | null;
  status: string | null;
  created_date: string | null;
  window_invoice_nos: string[];
  window_quoted_goods: string;
  window_surcharge_foregone: string;
}

export interface SalespersonRollup {
  salesperson_no: string;
  salesperson_name: string | null;
  groups: number;
  goods: string;
  expected_policy: string;
  billed_surcharge: string;
  shortfall: string;
  capture_pct: string | null;
}

export interface ClassTotal {
  classification: string;
  groups: number;
  gap: string;
  counts_toward_shortfall: boolean;
}

export interface ReconciliationReport {
  generated_at: string;
  window_start: string;
  window_end: string;
  options: SurchargeOptions;
  invoices_in: number;
  invoices_cm: number;
  groups_in_scope: number;
  goods: string;
  expected_gross: string;
  expected_policy: string;
  billed_surcharge: string;
  surcharge_billed_all_lines: string;
  unattributed_surcharge: string;
  capture_pct: string | null;
  shortfall: string;
  exemption_foregone_customer: string;
  exemption_foregone_quote: string;
  /** Goods × percent skipped for per-location exempt ship-tos; absent on older persisted reports. */
  exemption_foregone_ship_to?: string;
  /** Mirrored sales-site exemption events (who/when) the run could replay; null = mirror not loaded. */
  events_rows?: number | null;
  class_totals: ClassTotal[];
  groups: OrderGroup[];
  exempt_customers: ExemptCustomerRow[];
  exempt_quotes: ExemptQuoteRow[];
  by_salesperson: SalespersonRollup[];
  exemption_basis_counts: Record<string, number>;
  history_rows: number;
  history_earliest_valid_from: string | null;
  caveats: string[];
  /** False when chemmas_sales.order_invoice_queue could not be read in this run. */
  invoice_queue_readable: boolean;
}

export interface SurchargeRunDetail extends SurchargeRunSummary {
  report: ReconciliationReport | null;
}

export interface SurchargeRunListResponse {
  runs: SurchargeRunSummary[];
  total: number;
}

export interface SurchargeRunQueued {
  status: string;
  detail: string;
  task_id: string | null;
}

const BASE = '/admin/surcharge-reconciliation';

export const surchargeReconciliationApi = {
  listRuns: async (limit = 20): Promise<SurchargeRunListResponse> => {
    const r = await api.get<SurchargeRunListResponse>(`${BASE}/runs`, { params: { limit } });
    return r.data;
  },
  latest: async (): Promise<SurchargeRunDetail> => {
    const r = await api.get<SurchargeRunDetail>(`${BASE}/runs/latest`);
    return r.data;
  },
  getRun: async (runId: string): Promise<SurchargeRunDetail> => {
    const r = await api.get<SurchargeRunDetail>(`${BASE}/runs/${runId}`);
    return r.data;
  },
  queueRun: async (window?: { window_start?: string; window_end?: string }): Promise<SurchargeRunQueued> => {
    const r = await api.post<SurchargeRunQueued>(`${BASE}/runs`, window ?? {});
    return r.data;
  },
};
